import type { CompositionDocument, CompositionPublication } from './document';

export type RepertoireProcessingStatus = 'processing' | 'processed' | 'error' | 'deleted';

/**
 * Decides which operation identifies the next publish attempt.
 *
 * A retry of the same revision reuses the previous operation so the backend replays
 * its original result instead of creating a second repertoire item. Publishing a
 * newer revision is a genuinely new operation and gets a fresh identifier.
 */
export function nextPublicationAttempt(
  document: CompositionDocument,
  makeOperationId: () => string = () => crypto.randomUUID(),
): CompositionPublication {
  const previous = document.publication;
  const retryingSameRevision =
    previous !== undefined &&
    previous.publishedRevision === document.revision &&
    previous.status !== 'processed';
  return {
    operationId: retryingSameRevision ? previous.operationId : makeOperationId(),
    publishedRevision: document.revision,
    status: 'pending',
    updatedAt: new Date().toISOString(),
    ...(retryingSameRevision && previous.repertoireId
      ? { repertoireId: previous.repertoireId }
      : {}),
  };
}

export const publicationWithResult = (
  attempt: CompositionPublication,
  result: { repertoireId: string; status: RepertoireProcessingStatus },
): CompositionPublication => ({
  ...attempt,
  repertoireId: result.repertoireId,
  status: result.status === 'error' || result.status === 'deleted' ? 'error' : result.status,
  updatedAt: new Date().toISOString(),
  message: undefined,
});

export const publicationWithError = (
  attempt: CompositionPublication,
  message: string,
): CompositionPublication => ({
  ...attempt,
  status: 'error',
  message,
  updatedAt: new Date().toISOString(),
});

export type PublicationState =
  | { kind: 'unpublished' }
  | { kind: 'publishing' }
  | { kind: 'processing'; repertoireId?: string }
  | { kind: 'published'; repertoireId?: string }
  | { kind: 'behind'; repertoireId?: string; revisionsAhead: number }
  | { kind: 'failed'; message?: string };

/**
 * What the composer should be told about publication right now. "Behind" matters
 * most: the piece is in the repertoire, but the edits made since are not, and
 * saying "Published" there would be false.
 */
export function getPublicationState(document: CompositionDocument): PublicationState {
  const publication = document.publication;
  if (!publication) return { kind: 'unpublished' };
  if (publication.status === 'error') return { kind: 'failed', message: publication.message };
  if (publication.status === 'pending') return { kind: 'publishing' };
  const revisionsAhead = document.revision - publication.publishedRevision;
  if (publication.status === 'processing')
    return { kind: 'processing', repertoireId: publication.repertoireId };
  if (revisionsAhead > 0)
    return { kind: 'behind', repertoireId: publication.repertoireId, revisionsAhead };
  return { kind: 'published', repertoireId: publication.repertoireId };
}

export function describePublicationState(state: PublicationState): string {
  switch (state.kind) {
    case 'publishing':
      return 'Adding to repertoire…';
    case 'processing':
      return 'In repertoire · preparing score';
    case 'published':
      return 'In repertoire · ready to practise';
    case 'behind':
      return state.revisionsAhead === 1
        ? 'In repertoire · 1 edit not published'
        : `In repertoire · ${state.revisionsAhead} edits not published`;
    case 'failed':
      return 'Could not add to repertoire';
    default:
      return 'Not in repertoire yet';
  }
}
